/* eslint-disable react/jsx-key */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import moment from 'moment';

import PhoneInputComponent from '../../common/components/phone-number-input';
import SignUpButtonComponent from './components/new-sign-up-button';
import {
    ISignUpEmailVerification,
    ISignUpGivenNameVerification,
    ISignUpItem,
    ISignUpLoading,
    ISignUpSurnameVerification,
    ISignUpViewProps
} from './new-sign-up';

import { format } from '@msdyn365-commerce-modules/utilities';
import { clearNewSignupDataLayerEvent } from '../../common/utils/datalayer';
import { validateEmail } from '../../common/utils/email-utils';
import { MIN_AGE_FOR_SIGNUP } from '../../themes/venchi/utils/constants';
import { PasswordRegex } from '@msdyn365-commerce-modules/retail-actions';

const SignUpItem: React.FC<ISignUpItem> = ({ wrapper, label, errorMessage, input }) => {
    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<ISignUpEmailVerification> = ({ email, buttonWrapper, buttons, successMessage, errorMessage }) => {
    return (
        <>
            {successMessage &&
                successMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <SignUpItem {...email} />
            {errorMessage &&
                errorMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <Node {...buttonWrapper}>
                {buttons &&
                    buttons.map((button: React.ReactNode) => {
                        return <>{button}</>;
                    })}
            </Node>
        </>
    );
};

/**
 * Handles validation of sign-up givenName.
 * @param event - Content editable event.
 * @param event.givenName - Event Given Name.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for Given Name.
 */
const SignUpGivenNameVerificationComponent: React.FC<ISignUpGivenNameVerification> = ({ givenName, errorMessage }) => {
    return (
        <>
            <SignUpItem {...givenName} />
            {errorMessage.map((message: React.ReactNode) => {
                return <>{message}</>;
            })}
        </>
    );
};

/**
 * Handles validation of signUp surname.
 * @param event - Content editable event.
 * @param event.surname - Event surname.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for surname.
 */
const SignUpSurnameVerificationComponent: React.FC<ISignUpSurnameVerification> = ({ surname, errorMessage }) => {
    return (
        <>
            <SignUpItem {...surname} />
            {errorMessage.map((message: React.ReactNode) => {
                return <>{message}</>;
            })}
        </>
    );
};

const LocalAccount: React.FC<ISignUpViewProps> = props => {
    const {
        localAccount,
        items,
        emailVerification,
        givenNameVerification,
        surnameVerification,
        buttons,
        errorMessage,
        disclaimer
    } = props.signUpLocalAccount;

    const locale = props.locale;
    const regionCRM = props.regionCRM;
    const EcomSignUpLegalEntity = props.EcomSignUpLegalEntity;
    let LastConsentSource: string;

    const currency = props?.signUp?.moduleProps?.context?.request?.channel?.Currency;
    const currentCountry = currency === 'EUR' && locale === 'en-GB' ? 'eu' : locale.replace('-', '');

    const postalCodeValidators: { [key: string]: RegExp } = React.useMemo(
        () => ({
            it: /^\d{5}$/,
            fr: /^\d{5}$|^\d{2} \d{3}$/,
            de: /^\d{5}$/,
            eu: /^[A-Z0-9]{2,10}$/i,
            enGB: /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/,
            enAU: /^\d{4}$/,
            enSG: /^\d{6}$/,
            // eslint-disable-next-line security/detect-unsafe-regex
            enUS: /^\d{5}(-\d{4})?$/
        }),
        []
    );

    switch (props.LastConsentSource) {
        case 'FinanceOperationEU':
            LastConsentSource = 'Finance&Operation EU';
            break;
        case 'FinanceOperationUS':
            LastConsentSource = 'Finance&Operation US';
            break;
        default:
            LastConsentSource = 'Finance&Operation EU';
    }

    const [showPassword, setShowPassword] = React.useState({
        passwordField: false,
        reenterPasswordField: false
    });

    // This error is shown under the register button.
    const [phoneErrorMessage, setPhoneErrorMessage] = React.useState('');
    const [showPostalCodeError, setShowPostalCodeError] = React.useState<boolean>(false);
    const [showInvalidMailError, setShowInvalidMailError] = React.useState<boolean>(false);
    const [showMinAgeError, setShowMinAgeError] = React.useState<boolean>(false);
    const [showInvalidPasswordError, setShowInvalidPasswordError] = React.useState<boolean>(false);

    const checkPostalCode = React.useCallback((postalCode: string) => {
        if (!postalCode) {
            setShowPostalCodeError(false);
            return;
        }

        setShowPostalCodeError(!postalCodeValidators[currentCountry].test(postalCode));
    }, []);

    const containsCurrency = (password: string) => {
        const pattern = /\p{Sc}/u;
        return pattern.test(password);
    };

    const isValidPassword = (password: string) => {
        console.log(`Regex match: ${PasswordRegex.defaultRegex.test(password)}`);
        console.log(`Contains currency: ${containsCurrency(password)}`);
        return PasswordRegex.defaultRegex.test(password) && !containsCurrency(password);
    };

    if (typeof window !== 'undefined') {
        const signUpButton: HTMLElement = document.getElementById('continue') as HTMLElement;
        if (signUpButton !== undefined) {
            signUpButton.onclick = e => {
                e.preventDefault();
                e.stopPropagation();

                clearNewSignupDataLayerEvent();
            };
        }
    }

    const profiling_ref = React.useRef<HTMLInputElement>(null);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (locale.toLowerCase() === 'en-us') {
            const { checked } = e.target;
            profiling_ref.current!.checked = checked;
        }
    };

    const currentChannelCountryRegionISOCode = props.channelCountryRegionISOCode;

    // DONE guest state session variable; check that user ID (the one that's NOT an FC) exists alongside the user not being auth (since if not auth = loyalty)

    return (
        <Node {...localAccount}>
            {items &&
                items.map((item: ISignUpItem) => {
                    switch (item.key) {
                        case 'email':
                            if (emailVerification.isRequired) {
                                return (
                                    <Node {...emailVerification.verificationControlWrapper}>
                                        <SignUpItem {...item} />
                                        <SignUpEmailVerification {...emailVerification} />
                                    </Node>
                                );
                            } else {
                                const emailRepErrorText: string = props.resources.emailRepErrorText ?? null;
                                return (
                                    <>
                                        <SignUpItem
                                            key={item.key}
                                            wrapper={item.wrapper}
                                            label={item.label}
                                            errorMessage={item.errorMessage}
                                            input={
                                                <input
                                                    type='email'
                                                    id={item.key}
                                                    name={item.key}
                                                    required
                                                    onChange={() => {
                                                        if (typeof window !== 'undefined') {
                                                            const email: HTMLInputElement = document.getElementById(
                                                                'email'
                                                            ) as HTMLInputElement;
                                                            const email_rep: HTMLInputElement = document.getElementById(
                                                                'email_rep'
                                                            ) as HTMLInputElement;
                                                            const email_rep_error = document.getElementById(
                                                                'text-error'
                                                            ) as HTMLParagraphElement;
                                                            const email_rep_error_backup = document.getElementById(
                                                                'text-error-backup'
                                                            ) as HTMLSpanElement;

                                                            setShowInvalidMailError(email.value ? !validateEmail(email.value) : false);

                                                            email_rep.pattern = email.value;
                                                            // I hope the following works well with Azure
                                                            email_rep.title = emailRepErrorText;

                                                            if (email.value === email_rep.value) {
                                                                if (email_rep_error !== null) {
                                                                    email_rep_error.textContent = ' ';
                                                                } else {
                                                                    email_rep_error_backup.textContent = ' ';
                                                                }
                                                            } else {
                                                                if (email_rep_error !== null) {
                                                                    email_rep_error.textContent = emailRepErrorText;
                                                                } else {
                                                                    email_rep_error_backup.textContent = emailRepErrorText;
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            }
                                        />
                                        {showInvalidMailError && (
                                            <div
                                                className='ms-sign-up__item-error error itemLevel ms-sign-up__item-error--postal-code'
                                                role='alert'
                                                aria-live='assertive'
                                                aria-hidden='true'
                                            >
                                                <p className='ms-sign-up__item-error-text'>{props.resources.invalidEmailError}</p>
                                            </div>
                                        )}
                                        <div className='ms-sign-up__account-item ms-sign-up__account-item-email_rep entry-item attrEntry'>
                                            <label id='email_rep_label' htmlFor='email_rep' className='ms-sign-up__account-item-label'>
                                                {props.resources.emailRepeatLabelText
                                                    ? props.resources.emailRepeatLabelText
                                                    : 'Repeat your email address *'}
                                            </label>
                                            <input
                                                type='text'
                                                id='email_rep'
                                                name='email_rep'
                                                required
                                                maxLength={255}
                                                className='ms-sign-up__account-item-input ms-sign-up__account-item-email_rep'
                                                onChange={() => {
                                                    const email: HTMLInputElement = document.getElementById('email') as HTMLInputElement;
                                                    const email_rep: HTMLInputElement = document.getElementById(
                                                        'email_rep'
                                                    ) as HTMLInputElement;
                                                    const email_rep_error = document.getElementById('text-error') as HTMLParagraphElement;
                                                    const email_rep_error_backup = document.getElementById(
                                                        'text-error-backup'
                                                    ) as HTMLSpanElement;

                                                    email.pattern = email_rep.value;

                                                    if (email.value === email_rep.value) {
                                                        if (email_rep_error !== null) {
                                                            email_rep_error.textContent = ' ';
                                                        } else {
                                                            email_rep_error_backup.textContent = ' ';
                                                        }
                                                    } else {
                                                        if (email_rep_error !== null) {
                                                            email_rep_error.textContent = emailRepErrorText;
                                                        } else {
                                                            email_rep_error_backup.textContent = emailRepErrorText;
                                                        }
                                                    }
                                                }}
                                            />
                                            <div
                                                className='ms-sign-up__item-error error itemLevel'
                                                role='alert'
                                                aria-live='assertive'
                                                aria-hidden='true'
                                            >
                                                {/* <p id='text-error' className='ms-sign-up__item-error-text'></p> */}
                                                <p id='text-error' style={{ color: '#C13533' }}></p>
                                            </div>
                                            <span id='text-error-backup' style={{ color: '#C13533' }}></span>
                                        </div>
                                    </>
                                );
                            }
                        case 'newPassword':
                            const passwordRepErrorText: string = props.resources.passwordRepErrorText ?? null;
                            return (
                                <>
                                    <SignUpItem
                                        key={item.key}
                                        wrapper={item.wrapper}
                                        label={item.label}
                                        errorMessage={item.errorMessage}
                                        input={
                                            <>
                                                <div className='input-group'>
                                                    <input
                                                        type={showPassword.passwordField ? 'text' : 'password'}
                                                        className='ms-sign-up__account-item-input ms-sign-up__account-item-newPassword input-group__text'
                                                        id={item.key}
                                                        name={item.key}
                                                        required
                                                        onChange={() => {
                                                            if (typeof window !== 'undefined') {
                                                                const newPassword: HTMLInputElement = document.getElementById(
                                                                    'newPassword'
                                                                ) as HTMLInputElement;
                                                                const reenterPassword: HTMLInputElement = document.getElementById(
                                                                    'reenterPassword'
                                                                ) as HTMLInputElement;
                                                                const reenterPassword_error = document.getElementById(
                                                                    'password-text-error'
                                                                ) as HTMLParagraphElement;
                                                                const reenterPassword_error_backup = document.getElementById(
                                                                    'password-text-error-backup'
                                                                ) as HTMLSpanElement;

                                                                reenterPassword.pattern = newPassword.value;
                                                                // I hope the following works well with Azure
                                                                reenterPassword.title = passwordRepErrorText;

                                                                setShowInvalidPasswordError(!isValidPassword(newPassword.value));

                                                                if (newPassword.value === reenterPassword.value) {
                                                                    if (reenterPassword_error !== null) {
                                                                        reenterPassword_error.textContent = ' ';
                                                                    } else {
                                                                        reenterPassword_error_backup.textContent = ' ';
                                                                    }
                                                                } else {
                                                                    if (reenterPassword_error !== null) {
                                                                        reenterPassword_error.textContent = passwordRepErrorText;
                                                                    } else {
                                                                        reenterPassword_error_backup.textContent = passwordRepErrorText;
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        onClick={() =>
                                                            setShowPassword(curState => ({
                                                                ...curState,
                                                                passwordField: !curState.passwordField
                                                            }))
                                                        }
                                                        className='input-group__icon-button'
                                                        type='button'
                                                    >
                                                        <i
                                                            aria-label={
                                                                showPassword.passwordField
                                                                    ? props.resources.hidePasswordLabel
                                                                    : props.resources.showPasswordLabel
                                                            }
                                                            className={`input-group__icon ${
                                                                showPassword.passwordField
                                                                    ? 'input-group__icon--eye-off'
                                                                    : 'input-group__icon--eye'
                                                            }`}
                                                        />
                                                    </button>
                                                </div>
                                                {showInvalidPasswordError && (
                                                    <div
                                                        className='ms-sign-up__item-error error itemLevel ms-sign-up__item-error--postal-code'
                                                        role='alert'
                                                        aria-live='assertive'
                                                        aria-hidden='true'
                                                    >
                                                        <p className='ms-sign-up__item-error-text'>
                                                            {props.resources.invalidPasswordInlineError}
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        }
                                    />
                                    <div className='ms-sign-up__account-item ms-sign-up__account-item-reenterPassword entry-item attrEntry'>
                                        <label
                                            id='reenterPassword_label'
                                            htmlFor='reenterPassword'
                                            className='ms-sign-up__account-item-label'
                                        >
                                            {props.resources.confirmPasswordLabelText
                                                ? props.resources.confirmPasswordLabelText
                                                : 'Confirm password *'}
                                        </label>
                                        <div className='input-group'>
                                            <input
                                                type={showPassword.reenterPasswordField ? 'text' : 'password'}
                                                id='reenterPassword'
                                                name='reenterPassword'
                                                required
                                                maxLength={255}
                                                className='ms-sign-up__account-item-input ms-sign-up__account-item-reenterPassword input-group__text'
                                                onChange={() => {
                                                    const newPassword: HTMLInputElement = document.getElementById(
                                                        'newPassword'
                                                    ) as HTMLInputElement;
                                                    const reenterPassword: HTMLInputElement = document.getElementById(
                                                        'reenterPassword'
                                                    ) as HTMLInputElement;
                                                    const reenterPassword_error = document.getElementById(
                                                        'password-text-error'
                                                    ) as HTMLParagraphElement;
                                                    const reenterPassword_error_backup = document.getElementById(
                                                        'password-text-error-backup'
                                                    ) as HTMLSpanElement;

                                                    newPassword.pattern = reenterPassword.value;

                                                    if (newPassword.value === reenterPassword.value) {
                                                        if (reenterPassword_error !== null) {
                                                            reenterPassword_error.textContent = ' ';
                                                        } else {
                                                            reenterPassword_error_backup.textContent = ' ';
                                                        }
                                                    } else {
                                                        if (reenterPassword_error !== null) {
                                                            reenterPassword_error.textContent = passwordRepErrorText;
                                                        } else {
                                                            reenterPassword_error_backup.textContent = passwordRepErrorText;
                                                        }
                                                    }
                                                }}
                                            />
                                            <button
                                                onClick={() =>
                                                    setShowPassword(curState => ({
                                                        ...curState,
                                                        reenterPasswordField: !curState.reenterPasswordField
                                                    }))
                                                }
                                                className='input-group__icon-button'
                                                type='button'
                                            >
                                                <i
                                                    aria-label={
                                                        showPassword.reenterPasswordField
                                                            ? props.resources.hidePasswordLabel
                                                            : props.resources.showPasswordLabel
                                                    }
                                                    className={`input-group__icon ${
                                                        showPassword.reenterPasswordField
                                                            ? 'input-group__icon--eye-off'
                                                            : 'input-group__icon--eye'
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                        <div
                                            className='ms-sign-up__item-error error itemLevel'
                                            role='alert'
                                            aria-live='assertive'
                                            aria-hidden='true'
                                        >
                                            <p id='password-text-error' style={{ color: '#C13533' }}></p>
                                        </div>
                                        <span id='password-text-error-backup' style={{ color: '#C13533' }}></span>
                                    </div>
                                </>
                            );
                        case 'givenName':
                            if (givenNameVerification.isRequired) {
                                return (
                                    <Node {...givenNameVerification.verificationControlWrapper}>
                                        <SignUpItem {...item} />
                                        <SignUpGivenNameVerificationComponent {...givenNameVerification} />
                                    </Node>
                                );
                            } else {
                                return <SignUpItem {...item} />;
                            }
                        case 'surname':
                            if (surnameVerification.isRequired) {
                                return (
                                    <Node {...surnameVerification.verificationControlWrapper}>
                                        <SignUpItem {...item} />
                                        <SignUpSurnameVerificationComponent {...surnameVerification} />
                                    </Node>
                                );
                            } else {
                                return <SignUpItem {...item} />;
                            }
                        case 'extension_Birthday':
                            return (
                                <>
                                    <div className={'ms-sign-up__account-item ms-sign-up__account-item-birthday entry-item attrEntry'}>
                                        <label className={'ms-sign-up__account-item-label'} htmlFor='birthday'>
                                            {props.resources.birthdayLabelText ? props.resources.birthdayLabelText : null}
                                        </label>
                                        {/* // TODO? Validate that it's not in the future, and possibly that it's after at least 18 years */}
                                        <input
                                            type='date'
                                            id='birthday'
                                            name='birthday'
                                            className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday'
                                            onPaste={e => e.preventDefault()}
                                            onChange={() => {
                                                if (typeof window !== 'undefined') {
                                                    const birthday_proxy: HTMLInputElement = document.getElementById(
                                                        'birthday'
                                                    ) as HTMLInputElement;
                                                    const birthday_hidden: HTMLInputElement = document.getElementById(
                                                        'extension_Birthday'
                                                    ) as HTMLInputElement;

                                                    const birthdayValue = birthday_proxy.value;
                                                    const birthdayDate = moment(birthdayValue, 'YYYY-MM-DD');
                                                    const today = moment();
                                                    const age = today.diff(birthdayDate, 'years');

                                                    birthday_proxy.onchange = event => {
                                                        birthday_hidden.value = birthday_proxy.value;
                                                    };

                                                    if (age < MIN_AGE_FOR_SIGNUP) {
                                                        setShowMinAgeError(true);
                                                        return;
                                                    }

                                                    setShowMinAgeError(false);
                                                }
                                            }}
                                        />
                                        <input
                                            type='text'
                                            id='extension_Birthday'
                                            name='extension_Birthday'
                                            className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday'
                                            onPaste={e => e.preventDefault()}
                                            style={{ display: 'none' }}
                                        />
                                        {showMinAgeError && (
                                            <div
                                                className='ms-sign-up__item-error error itemLevel ms-sign-up__item-error--postal-code'
                                                role='alert'
                                                aria-live='assertive'
                                                aria-hidden='true'
                                            >
                                                <p className='ms-sign-up__item-error-text'>{props.resources.minAgeErrorLabel}</p>
                                            </div>
                                        )}
                                    </div>
                                    {props.config.BirthdayText ? (
                                        <Msdyn365.RichTextComponent className='extra-text__birthday' text={props.config.BirthdayText} />
                                    ) : null}
                                </>
                            );
                        case 'postalCode':
                            return (
                                <>
                                    <SignUpItem
                                        wrapper={item.wrapper}
                                        key={item.key}
                                        label={item.label}
                                        errorMessage={item.errorMessage}
                                        input={
                                            <input
                                                type='text'
                                                id='new-postalCode'
                                                name='new-postalCode'
                                                className='ms-sign-up__account-item-input ms-sign-up__account-item-postalCode'
                                                aria-required='true'
                                                maxLength={8}
                                                onChange={e => {
                                                    const postalCodeHidden: HTMLInputElement = document.getElementById(
                                                        'postalCode'
                                                    ) as HTMLInputElement;

                                                    postalCodeHidden.value = e.target.value;

                                                    checkPostalCode(e.target.value);
                                                }}
                                            ></input>
                                        }
                                    />
                                    <input
                                        type='hidden'
                                        id='postalCode'
                                        name='postalCode'
                                        className='ms-sign-up__account-item-input ms-sign-up__account-item-phone_number'
                                        style={{ display: 'none' }}
                                    />
                                    {showPostalCodeError && (
                                        <div
                                            className='ms-sign-up__item-error error itemLevel ms-sign-up__item-error--postal-code'
                                            role='alert'
                                            aria-live='assertive'
                                            aria-hidden='true'
                                        >
                                            <p className='ms-sign-up__item-error-text'>{props.resources.postalCodeErrorMessage}</p>
                                        </div>
                                    )}
                                    {props.config.PostalText ? (
                                        <Msdyn365.RichTextComponent className='extra-text__postalcode' text={props.config.PostalText} />
                                    ) : null}
                                </>
                            );
                        case 'phone_number':
                            return (
                                <>
                                    <SignUpItem
                                        wrapper={item.wrapper}
                                        key={item.key}
                                        label={item.label}
                                        errorMessage={item.errorMessage}
                                        input={
                                            <PhoneInputComponent
                                                channelCountryRegionISOCode={currentChannelCountryRegionISOCode}
                                                isCountryCodeHidden={props.config.hideCountryCodeSelector || false}
                                                phoneInputId={item.key}
                                                resources={{
                                                    phoneValidationErrorMin: props.resources.phoneValidationErrorMin,
                                                    phoneValidationErrorMax: props.resources.phoneValidationErrorMax,
                                                    phoneValidationErrorNumeric: props.resources.phoneValidationErrorNumeric
                                                }}
                                                onPhoneNumberChange={(
                                                    phoneNumber: string,
                                                    fullPhoneNumber: string,
                                                    hasValidationError: boolean
                                                ) => {
                                                    if (!hasValidationError) {
                                                        setPhoneErrorMessage('');
                                                    } else {
                                                        setPhoneErrorMessage(props.resources.phoneValidationErrorMessage);
                                                    }
                                                    const phoneNumberHidden: HTMLInputElement = document.getElementById(
                                                        'extension_PhoneNumber'
                                                    ) as HTMLInputElement;

                                                    phoneNumberHidden.value = phoneNumber ? fullPhoneNumber : '';
                                                }}
                                            />
                                        }
                                    />
                                    <input
                                        type='text'
                                        id='extension_PhoneNumber'
                                        name='extension_PhoneNumber'
                                        className='ms-sign-up__account-item-input ms-sign-up__account-item-phone_number'
                                        style={{ display: 'none' }}
                                    />
                                    {props.config.PhoneText ? (
                                        <Msdyn365.RichTextComponent className='extra-text__phone' text={props.config.PhoneText} />
                                    ) : null}
                                </>
                            );
                        case 'extension_GenericMarketing':
                            return (
                                <>
                                    <div className={`inputBox privacy_field_ctn ${props.config.hideMarketing && 'hidden'}`}>
                                        <label>
                                            <input
                                                type='checkbox'
                                                id='extension_GenericMarketing'
                                                name='extension_GenericMarketing'
                                                value={'true'}
                                                defaultChecked={props.config.preflagMarketing}
                                                onChange={handleChange}
                                            />
                                            {props?.resources?.htmlGenericMarketing &&
                                            props.config.privacyPolicyURL &&
                                            locale.toLowerCase() !== 'en-us' ? (
                                                <p
                                                    className='privacy-disclaimer-M'
                                                    dangerouslySetInnerHTML={{
                                                        __html: format(
                                                            props.config.useCrossCountryLabels
                                                                ? props?.resources?.htmlGenericMarketingCrossCountry
                                                                : props?.resources?.htmlGenericMarketing,
                                                            props.config.privacyPolicyURL
                                                        )
                                                    }}
                                                />
                                            ) : (
                                                props.config.USmarketingText && (
                                                    <Msdyn365.RichTextComponent
                                                        className='privacy-disclaimer-M'
                                                        text={props?.config.USmarketingText}
                                                    />
                                                )
                                            )}
                                        </label>
                                    </div>
                                </>
                            );
                        case 'extension_Profiling':
                            return (
                                <>
                                    <div className={`inputBox privacy_field_ctn ${props.config.hideProfiling && 'hidden'}`}>
                                        <label>
                                            <input
                                                type='checkbox'
                                                id='extension_Profiling'
                                                name='extension_Profiling'
                                                value={'true'}
                                                defaultChecked={props.config.preflagProfiling}
                                                ref={profiling_ref}
                                            />
                                            {props?.resources?.htmlProfiling && props.config.privacyPolicyURL ? (
                                                <p
                                                    className='privacy-disclaimer-P'
                                                    dangerouslySetInnerHTML={{
                                                        __html: format(
                                                            props.config.useCrossCountryLabels
                                                                ? props?.resources?.htmlProfilingCrossCountry
                                                                : props?.resources?.htmlProfiling,
                                                            props.config.privacyPolicyURL
                                                        )
                                                    }}
                                                />
                                            ) : null}
                                        </label>
                                    </div>
                                </>
                            );
                        case 'extension_ThirdParty':
                            return (
                                <>
                                    <div className={`inputBox privacy_field_ctn ${props.config.hideThirdParty && 'hidden'}`}>
                                        <label>
                                            <input
                                                type='checkbox'
                                                id='extension_ThirdParty'
                                                name='extension_ThirdParty'
                                                value={'true'}
                                                defaultChecked={props.config.preflagThirdParty}
                                            />
                                            {props?.resources?.htmlThirdParty && props.config.privacyPolicyURL ? (
                                                <p
                                                    className='privacy-disclaimer-T'
                                                    dangerouslySetInnerHTML={{
                                                        __html: format(
                                                            props.config.useCrossCountryLabels
                                                                ? props?.resources?.htmlThirdPartyCrossCountry
                                                                : props?.resources?.htmlThirdParty,
                                                            props.config.privacyPolicyURL
                                                        )
                                                    }}
                                                />
                                            ) : null}
                                        </label>
                                    </div>
                                </>
                            );
                        case 'extension_RegionCRM':
                            return (
                                <div className={'region_crm_ctn'}>
                                    <input type='text' name='extension_RegionCRM' id='extension_RegionCRM' value={regionCRM} />
                                </div>
                            );
                        case 'extension_LastConsentSource':
                            return (
                                <div className={'lastConsentSource_ctn'}>
                                    <input
                                        type='text'
                                        name='extension_LastConsentSource'
                                        id='extension_LastConsentSource'
                                        value={LastConsentSource}
                                    />
                                </div>
                            );
                        case 'extension_EcomSignUpLegalEntity':
                            return (
                                <div className={'ecomSignUpLegalEntity_ctn'}>
                                    <input
                                        type='text'
                                        name='extension_EcomSignUpLegalEntity'
                                        id='extension_EcomSignUpLegalEntity'
                                        value={EcomSignUpLegalEntity}
                                    />
                                </div>
                            );
                        default:
                            return <SignUpItem {...item} />;
                    }
                })}
            {disclaimer}
            {errorMessage &&
                errorMessage.map((error: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{error}</React.Fragment>;
                })}
            {buttons &&
                buttons.map((button: React.ReactNode, index: number) => {
                    // @ts-ignore
                    if (button?.key === 'continue') {
                        const isDisabled =
                            (phoneErrorMessage !== null && phoneErrorMessage !== undefined && phoneErrorMessage !== '') ||
                            showPostalCodeError ||
                            showInvalidMailError ||
                            showMinAgeError ||
                            showInvalidPasswordError;

                        return (
                            <React.Fragment key={index}>
                                <SignUpButtonComponent
                                    key='continue'
                                    id='continue' // @ts-ignore
                                    className={button.props.className} // @ts-ignore
                                    ariaLabel={button.props.ariaLabel} // @ts-ignore
                                    text={button.props.text}
                                    disabled={isDisabled}
                                />
                            </React.Fragment>
                        );
                    } else {
                        return <React.Fragment key={index}>{button}</React.Fragment>;
                    }
                })}
            {phoneErrorMessage && (
                <div className='ms-sign-up__item-error error itemLevel' role='alert'>
                    <p style={{ color: '#C13533' }}>{phoneErrorMessage}</p>
                </div>
            )}
        </Node>
    );
};

const SignUpLoading: React.FC<ISignUpLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewProps> = props => {
    const { signUp, viewState, loading, defaultAADConainer, aadConainer, heading, config } = props;

    return (
        <Module {...signUp}>
            {config.includeCloudflareZarazScript && <script src='https://zaraz.venchi.com/cdn-cgi/zaraz/i.js'></script>}
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <Node {...aadConainer}>
                {heading}
                {LocalAccount(props)}
            </Node>
        </Module>
    );
};

export default SignUpView;
